import styled from "@emotion/styled"
import { useDealer, useDealerName, useEstablishment } from "components/retailer/v2/Context"
import SubNav from "components/nav-v2/Sub"
import { useConsole } from "contexts/Console"
import { useEnv } from "contexts/Env"
import { useState } from "react"
import cldSrcSet from "components/retailer/cldSrcSet"
import { getRetailerTypes } from "./utils"
import { compile } from "path-to-regexp"
import getMediaQuery from "css/breakpoints"
import { useDictionary } from "contexts/Dictionary"
import { waTriggerEvent } from "components/analytics/DigitalDataLayer"

const Figure = styled.figure`
  img {
    max-height: 32px;
    max-width: 120px;
  }
`

const LogoBlock = styled.div`
  text-align: center;
  padding: 0px 0 10px;
  margin-top: -6px;

  ${getMediaQuery("m")} {
    padding: 0px 0 10px;
  }

  figure img {
    max-width: 65px;
    max-height: 38px;

    ${getMediaQuery("m")} {
      max-width: 85px;
      max-height: 50px;
    }
  }
`

const processCard = (props, dealer, establishement, { console }) => {
  const isServiceCenter = establishement?.type === "SERVICECENTER"
  const dealerInHref = dealer?.urlRolexSeo
  const establishementInURL = isServiceCenter ? null : establishement?.urlRolexSeo?.split("/")?.[1]

  let slug
  try {
    slug = compile(props.link)({
      dealer: dealerInHref,
      establishment: establishementInURL,
    })
    //console.log("processCard slug: ", slug)
  } catch {
    // not enough data to create a link
    return null
  }
  // if (dealer && dealer.nbrEstablishment === 1 && !props.link.includes(":establishement")) {
  //   // no group card
  //   //return null
  // }

  props.slug = slug
  return props
}

const SubNavRetailer = ({ children, toggle_label, ...props }) => {
  const console = useConsole()
  const { cn: isChina } = useEnv()
  const env = useEnv()
  const dealerName = useDealerName()
  const dealer = useDealer()
  const dictionary = useDictionary()
  const establishment = useEstablishment()
  const isDealer = establishment === null || establishment === undefined
  const isGroupPage = !establishment
  const isServiceCenter = establishment?.type === "SERVICECENTER"
  const isAffiliate = establishment?.isAffiliate && establishment?.type === "SERVICECENTER"

  const hasGroup = dealer?.nbrEstablishment > 1
  const retailerTypes = getRetailerTypes({ retailer: establishment || dealer })

  const retailerDetails = isGroupPage ? dealer : establishment
  const rswi = isGroupPage ? retailerDetails.defaultStoreImages : retailerDetails.rolexId

  const retailerAsset = rswi => ({
    alt: "",
    loading: "eager",
    sizes: "100vw",
    srcSet: cldSrcSet(`d_retailers:a_generic:minisite-store-map.jpg/q_auto/f_auto/v1/retailers/${rswi}/minisite-store-map.jpg`, { env }),
  })

  const staticAssets = {
    buying: {
      sources: [
        {
          public_id:
            "rolexcom/rolex-retailers/store-locator/retailer-mini-site/navigation/rolex-ertailers-buying-a-rolex-navigation_rolexapprentices_2211ra_0757.jpg",
        },
      ],
    },
    rcpo: {
      sources: [
        {
          public_id: "rolexcom/rolex-retailers/store-locator/retailer-mini-site/navigation/rolex-retailers-rcpo-navigation_cpo_2403fb_0011_062524_iso_03.jpg",
        },
      ],
    },
    servicing: {
      sources: [
        {
          public_id:
            "rolexcom/rolex-retailers/store-locator/retailer-mini-site/navigation/rolex-retailers-servicing-your-rolex-navigation_sav_geneve_19dh_051_xl.jpg",
        },
      ],
    },
  }

  //TODO readapt code
  //TODO add logo as a children ? to discuss

  let module = structuredClone(props.module ?? [])
  const cards = module
    .map(item => item.card_retailer)
    .filter(Boolean)
    .map(card => processCard(card, dealer, establishment, { console }))
    .filter(Boolean)

  const cardsFiltered = cards.filter(card => {
    const array = []

    card.heading.title = card.heading.title.replace("$name", dealerName)
    card.heading.kicker = card.heading.kicker.replace("$name", dealerName)
    card.heading.subtitle = card.heading.subtitle.replace("$name", dealerName)
    card.link = isDealer
      ? card.link.replace(":dealer", dealer?.urlRolexV7 || establishment?.urlRolexV7)
      : card.link
          .replace(":dealer/:establishment", establishment?.urlRolexV7 || dealer?.urlRolexV7)
          .replace(":dealer", dealer?.urlRolexV7 || establishment?.urlRolexV7)
          .replace(/\?/g, "")

    if (hasGroup) array.push(card.card_id === "group")

    !isServiceCenter && array.push(card.card_id === "store")
    isServiceCenter && array.push(card.card_id === "service-center")

    if (!isAffiliate) array.push(retailerTypes.includes(card.card_id))
    else array.push(card.card_id === "servicing") // affiliate service centers should only have access to servicing page
    // affiliate service centers should not have access to store page
    return array.some(Boolean)
  })

  module = module.reduce((acc, item) => {
    if (!item.card_retailer) acc.push(item)
    return acc
  }, [])

  // for (const card of cardsFiltered) {
  //   module.push({ card_retailer: card })
  // }

  const bentoItems = cardsFiltered.map(card => {
    let assets = null

    switch (card.card_id) {
      case "group":
        assets = retailerAsset(dealer?.defaultStoreImages || establishment?.rolexId)
        break

      case "store":
        assets = retailerAsset(rswi)
        break

      case "buying":
        assets = staticAssets.buying
        break

      case "rcpo":
        assets = staticAssets.rcpo
        break

      case "servicing":
      case "service-center":
        assets = staticAssets.servicing
        break

      default:
        assets = card?.image || null
        break
    }

    return {
      item: {
        label: card.heading.title,
        aria_label: card.heading.title,
        href: card.link,
        theme: {
          theme: "dark-theme",
        },
        assets,
      },
    }
  })

  let bentoGrids = []
  switch (bentoItems.length) {
    case 1: {
      bentoGrids.push({
        grid: {
          pattern: "xxxx",
        },
      })
      break
    }
    case 2: {
      bentoGrids.push({
        grid: {
          pattern: "xxyy",
        },
      })
      break
    }
    case 3: {
      bentoGrids.push({
        grid: {
          pattern: "xxyz",
        },
      })
      break
    }
    case 4: {
      bentoGrids.push({
        grid: {
          pattern: "xyza",
        },
      })
      break
    }
    case 5: {
      bentoGrids.push({
        grid: {
          pattern: "xxyz",
        }
      })
      bentoGrids.push({
        grid: {
          pattern: "xxyy",
        }
      })
      break
    }
    default: {
      break
    }
  }

  module.push({
    bento: {
      reference: [
        {
          _content_type_uid: "navigation_bento",
          grids: bentoGrids,
          items: bentoItems,
        },
      ],
    },
  })

  const vendor = isChina ? "baidu" : "gmap"
  const dealerPhone = establishment?.dealerPhone || establishment?.phone1 ? establishment?.dealerPhone || establishment?.phone1 : null
  const getDirections = {
    baidu: `http://api.map.baidu.com/marker?location=${establishment?.latBaidu},${establishment?.lngBaidu}&content=&title=${dealerName}&output=html&src=webapp.baidu.openAPIdemo`,
    gmap: `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`${dealerName} ${establishment?.lat},${establishment?.lng}`)}`,
  }
  const getDirectionUrl = vendor ? getDirections[vendor] : getDirections.gmap

  const handleOnClick = item => {
    if (item?.triggerAction) {
      waTriggerEvent({
        eventName: "retailerInteractions",
        eventType: "Click",
        retailerInfo: {
          id: establishment?.rolexId,
          type: establishment?.type.toLowerCase(),
          interaction: item.triggerAction,
          previewsComponent: "store locator map page",
        },
      })
    }
  }

  module.push({
    action_retailer: {
      label: dealerPhone,
      // aria_label: `tel:${dealerPhone}`,
      href: `tel:${dealerPhone}`,
      target: "_blank",
      rel: "noopener noreferrer",
      type: "call",
      show: !!dealerPhone,
      external: true,
      triggerAction: "retailerCall",
      dir: "ltr",
      onClick: item => handleOnClick(item),
    },
  })

  module.push({
    action_retailer: {
      type: "direction",
      label: dictionary.getDirection(),
      // aria_label: dictionary.getDirection(),
      href: `${getDirectionUrl}`,
      target: "_blank",
      rel: "noopener noreferrer",
      show: getDirectionUrl,
      external: true,
      triggerAction: "getDirections",
      dir: "ltr",
      onClick: item => handleOnClick(item),
    },
  })

  props.subnav_heading = establishment?.type === "STORE" ? dictionary.ourStore() : dictionary.ourServiceCenter()
  props.variant = "retailer"

  if (isGroupPage) return null

  console.log("SubNav:Retailer(%o)", module)
  return (
    <SubNav {...Object.assign(props, { module: module })}>
      <LogoBlock>
        {dealer?.logos?.black ? (
          <Figure>
            <img alt={dealerName} src={new URL(`/retailers/${dealer.logos.black}`, env.statics)} />
          </Figure>
        ) : (
          dealerName
        )}
      </LogoBlock>
    </SubNav>
  )
}

const processSubNav = (subNav, { console }) => {
  subNav._content_type_uid = "sub-navigation-retailer"
  return subNav
}

export default SubNavRetailer
export { processSubNav }
